import Phaser from 'phaser';
import { getBoardUnitSize } from '../utils/coordinates';
import { COLORS } from '../config/theme';
import { getDominoTextureKey } from '../config/dominoAssets';

/**
 * DominoTile — renders a single domino tile using a pre-loaded PNG sprite.
 *
 * PNG naming convention: "tile-{min}-{max}" where min ≤ max (canonical form).
 * When valueA > valueB the image is flipped so valueA always appears on the
 * left (horizontal) or top (vertical) side, matching the game's logical layout.
 *
 * Game-state indicators (highlight / select) are drawn as a thin overlay on
 * top of the PNG so the artwork is never modified.
 *
 * Public API is identical to the previous Graphics-based version so Hand.ts,
 * Board.ts and GameScene.ts require zero changes.
 */
export class DominoTile extends Phaser.GameObjects.Container {
  public valueA: number;
  public valueB: number;
  public isDouble: boolean;
  public orientation: 'horizontal' | 'vertical';
  public tileId: number;

  private img!: Phaser.GameObjects.Image;
  private overlay!: Phaser.GameObjects.Graphics;
  private isHighlighted = false;
  private isSelected = false;

  // Scale multiplier for hand tiles (applied on top of board unit size)
  private tileScale: number;

  // TEMPORARY — native 84×42 hand-tile experiment (see
  // src/config/handTileExperiment.ts). When set, draw() uses this key
  // instead of the production getDominoTextureKey() lookup. undefined for
  // every other caller (Board.ts) — zero behavior change when unset.
  private textureKeyOverride?: string;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    tileId: number,
    valueA: number,
    valueB: number,
    orientation: 'horizontal' | 'vertical' = 'horizontal',
    tileScale = 1,
    textureKeyOverride?: string
  ) {
    super(scene, x, y);

    this.tileId      = tileId;
    this.valueA      = valueA;
    this.valueB      = valueB;
    this.isDouble    = valueA === valueB;
    this.orientation = orientation;
    this.tileScale   = tileScale;
    this.textureKeyOverride = textureKeyOverride;

    this.draw();
    scene.add.existing(this);
  }

  // ─── Private Helpers ─────────────────────────────────────

  /**
   * True when the stored PNG has the values in the wrong order for this tile.
   * Files are named domino_${lo}-${hi}.png (canonical low-high order).
   * When valueA > valueB, the image must be flipped to put valueA on the left/top.
   */
  private get needsFlip(): boolean {
    return this.valueA > this.valueB;
  }

  /** Tile screen-space dimensions (accounts for orientation). */
  private tileDims(): { tileW: number; tileH: number } {
    const unit = getBoardUnitSize() * this.tileScale;
    const isH  = this.orientation === 'horizontal';
    return {
      tileW: isH ? unit * 2 : unit,
      tileH: isH ? unit     : unit * 2,
    };
  }

  // ─── Drawing ─────────────────────────────────────────────

  /** Full rebuild — called from constructor and setOrientation(). */
  private draw(): void {
    this.removeAll(true);

    const { tileW, tileH } = this.tileDims();
    const halfW = tileW / 2;
    const halfH = tileH / 2;
    const isH   = this.orientation === 'horizontal';

    // ── Drop shadow — rendered first (lowest z-order) so it sits behind the PNG.
    // Offset 1px right / 1.5px down at ~20% alpha; survives heavy downscaling.
    // Destroyed automatically by removeAll(true) on the next draw() call.
    const shadowR = Math.max(2, Math.min(tileW, tileH) * 0.09);
    const shadowG = this.scene.add.graphics();
    shadowG.fillStyle(0x000000, 0.20);
    shadowG.fillRoundedRect(-halfW + 1, -halfH + 1.5, tileW, tileH, shadowR);
    this.add(shadowG);

    // ── PNG image ──────────────────────────────────────────
    // Durable safety net: if a caller passes an override key (native-hand
    // experiment, or a Cosmetics skin override) whose texture failed to
    // load, silently fall back to the classic key instead of rendering
    // Phaser's "missing texture" placeholder. No-op when there's no
    // override — the requested key is then always the already-loaded
    // classic key.
    const classicKey = getDominoTextureKey(this.valueA, this.valueB);
    const requestedKey = this.textureKeyOverride ?? classicKey;
    const resolvedKey = this.scene.textures.exists(requestedKey) ? requestedKey : classicKey;
    const img = this.scene.add.image(0, 0, resolvedKey);

    if (isH) {
      // Horizontal: display the PNG at its natural landscape orientation.
      img.setDisplaySize(tileW, tileH);
      img.setFlipX(this.needsFlip);
    } else {
      // Vertical: the PNG is landscape (wide), rotate it 90° CW so it becomes
      // portrait.  setDisplaySize is given the PRE-rotation dimensions (still
      // landscape) — after the 90° rotation Phaser swaps width ↔ height on
      // screen, giving the correct portrait size.
      img.setDisplaySize(tileH, tileW);
      img.setAngle(90);
      // Phaser applies flip BEFORE rotation. Flipping X on the landscape image
      // (left↔right) becomes a top↔bottom swap after 90° CW rotation, so
      // valueA ends up on top and valueB on bottom.
      img.setFlipX(this.needsFlip);
    }

    this.img = img;
    this.add(img);

    // ── State overlay (highlight / select borders) ─────────
    const overlay = this.scene.add.graphics();
    this.overlay  = overlay;
    this.add(overlay);
    this.drawOverlay(tileW, tileH);

    // ── Interactive hit area ───────────────────────────────
    this.setSize(tileW, tileH);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-halfW, -halfH, tileW, tileH),
      Phaser.Geom.Rectangle.Contains
    );
  }

  /**
   * Redraws only the state overlay (highlight / select glow).
   * Much cheaper than a full draw() — no image recreation.
   */
  private drawOverlay(tileW?: number, tileH?: number): void {
    if (!this.overlay) return;

    const dims  = tileW !== undefined ? { tileW, tileH: tileH! } : this.tileDims();
    const w     = dims.tileW;
    const h     = dims.tileH;
    const halfW = w / 2;
    const halfH = h / 2;
    const radius = Math.max(3, Math.min(w, h) * 0.1);
    const bw     = Math.max(2, Math.min(w, h) * 0.05);

    this.overlay.clear();

    if (this.isSelected) {
      // Gold fill + gold border
      this.overlay.fillStyle(COLORS.goldDark, 0.18);
      this.overlay.fillRoundedRect(-halfW, -halfH, w, h, radius);
      this.overlay.lineStyle(bw, COLORS.goldDark, 1);
      this.overlay.strokeRoundedRect(-halfW, -halfH, w, h, radius);
    } else if (this.isHighlighted) {
      // Subtle green fill + green border — "this tile is playable"
      this.overlay.fillStyle(0x44cc66, 0.12);
      this.overlay.fillRoundedRect(-halfW, -halfH, w, h, radius);
      this.overlay.lineStyle(bw * 0.8, 0x44cc66, 0.9);
      this.overlay.strokeRoundedRect(-halfW, -halfH, w, h, radius);
    } else {
      // Default: 1px dark edge border — keeps tile edges readable on green felt at small scale.
      // Warm near-black matches ivory tile border; 40% alpha keeps it subtle.
      this.overlay.lineStyle(1, 0x1a1208, 0.40);
      this.overlay.strokeRoundedRect(-halfW, -halfH, w, h, radius);
    }
  }

  // ─── Public API ──────────────────────────────────────────

  /** Change orientation and rebuild the tile image. */
  setOrientation(orientation: 'horizontal' | 'vertical'): void {
    this.orientation = orientation;
    this.draw();
  }

  /**
   * Rebuild visual dimensions at the current BOARD_UNIT_SIZE without changing
   * any canonical or transient state (values, orientation, tileScale, highlight,
   * selection, alpha, depth, scale, or external input listeners).
   * Call after BOARD_UNIT_SIZE changes so the PNG display size, shadow, overlay,
   * and hit area all match the new unit.
   */
  redraw(): void {
    this.draw();
  }

  /** Mark tile as playable (green glow). */
  highlight(): void {
    if (this.isHighlighted) return;
    this.isHighlighted = true;
    this.drawOverlay();
  }

  /** Remove playable indicator. */
  unhighlight(): void {
    if (!this.isHighlighted) return;
    this.isHighlighted = false;
    this.drawOverlay();
  }

  /** Mark tile as selected (gold glow + subtle lift). */
  select(): void {
    if (this.isSelected) return;
    this.isSelected = true;
    this.drawOverlay();
    if (!this.scene?.tweens) return;
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets:  this,
      scaleX:   1.08,
      scaleY:   1.08,
      y:        this.y - 6,
      duration: 100,
      ease:     'Back.easeOut',
    });
  }

  /** Remove selected state (return to normal position). */
  deselect(canonicalY?: number): void {
    if (!this.isSelected) return;
    this.isSelected = false;
    this.drawOverlay();
    if (!this.scene?.tweens) return;
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets:  this,
      scaleX:   1,
      scaleY:   1,
      y:        canonicalY ?? this.y + 6,
      duration: 100,
      ease:     'Power2',
    });
  }

  /** No-op. The "fade non-playable tiles to 40% alpha" cue was removed by
   *  design — the green highlight border on PLAYABLE tiles is the sole
   *  playability signal now, and the transparency read poorly on the new
   *  hand artwork. Kept as a method so callers need not change. */
  dim(): void {
    /* intentionally does nothing — see doc comment */
  }

  /** Restore full opacity. */
  undim(): void {
    this.setAlpha(1);
  }

  /** Smooth movement tween. */
  animateTo(x: number, y: number, duration = 400): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        x,
        y,
        duration,
        ease: 'Power2',
        onComplete: () => resolve(),
      });
    });
  }

  /** Heavy settle when a tile lands on the board: 1.06 → 1.0, like being
   *  pressed onto the table. Subtler than the old 0.82 grow-pop. */
  animatePlace(onSettle?: () => void): void {
    // Scene may be gone if the round ended while a tween was in flight.
    if (!this.active || !this.scene?.tweens) { onSettle?.(); return; }
    this.setScale(1.06);
    this.scene.tweens.add({
      targets:    this,
      scaleX:     1,
      scaleY:     1,
      duration:   220,
      ease:       'Cubic.easeOut',
      onComplete: onSettle,
    });
  }
}
